import React, { ComponentType, ReactNode } from 'react';
import ReactModal, { Props as ReactModalProps } from 'react-modal';
import memoize from 'memoize-one';

import { DefaultTheme } from 'styled-components';
import closeModalById from 'ia-react-core/~/actions/modal-fn/closeModalById';
import { StringKeyOf } from 'ia-react-core/~/interfaces/StringKeyOf';
import getAppContainerId from 'ia-react-core/~/utils/dom/getAppContainerId';
import Row from '../../grid/Row';
import Col from '../../grid/Col';

import get from '../../../utils/accessors/get';
import findChildByType from '../../../utils/components/_findChildByType';
import excludeChildrenByType from '../../../utils/components/_excludeChildrenByType';
import getPropByType from '../../../utils/components/_getPropByType';

import ModalContext from '../_ModalContext';
import { ModalGlobalStyles, StyledModalHeader, StyledHeaderIcon, StyledModalContent, StyledModalContentWrapper, StyledCloseButton, StyledFooter, StyledContentHeader } from './Modal.styles';
import { Header, Content, Footer } from './Modal.compounds';
import { MAX_MODAL_WIDTH } from './Modal.constants';

export interface ModalProps<Data = {}> extends Omit<ReactModalProps, 'children' | 'data' | 'isOpen' | 'style' | 'role' | 'className'> {
  children?: ReactNode | ((renderProps: ModalRenderProps<Data>) => ReactNode);
  /** The name of the modal. If provided, it will be used as the reducer key. Otherwise the reducer key will be "Modal${modalId}". */
  name?: string;
  /** The content of the title bar */
  modalTitle?: ReactNode;
  /** Icon class to display to the left of the modal header. */
  Icon?: ComponentType;
  /** Removes the padding of the content and footer regions */
  nopadding?: boolean;
  /** Defines if the modal can be closed by either a close button, clicking outside or ESC key */
  isClosable?: boolean;
  /** Hides the header  */
  noheader?: boolean;
  /** Sets the initial value of `isOpened`  */
  initialIsOpened?: boolean;
  /** Header background style */
  headerBackground?: string;
  /** Controls if the Modal is opened or closed. Marks this modal as being "controlled". A controlled modal won't integrate with Redux nor listen to actions. */
  isOpened?: boolean;
  /** Indicates that this tab should always be rendered. */
  forceRender?: boolean;
  /** Clicking outside the modal closes the modal. Defaults to "isClosable" value. */
  shouldCloseOnOverlayClick?: boolean;
  /** Pressing the ESC key closes the modal. Defaults to "isClosable" value.  */
  shouldCloseOnEsc?: boolean;
  /** Override the max modal width.  */
  maxWidth?: number;
  /** Defines the alignment of the text, title and buttons. */
  align?: 'left' | 'center' | 'right';
  /** Use scrolling inside the modal intead of outside. */
  scrollInsideModal?: boolean;
  /** Callback when the modal opens */
  onOpen?: () => void;
  /** Callback when the modal closes */
  onClose?: () => void;
  /** @ignore */
  'data-analytics-scope'?: string;
  /** @ignore */
  modalId?: number;
}

export interface ModalRenderProps<Data> {
  /** The name of the modal. If provided, it will be used as the reducer key. Otherwise the reducer key will be "Modal${modalId}". */
  name: string;
  /** @ignore */
  modalId: number;
  /** @ignore */
  data: Data;
}

interface ModalInternalsProps<Data> {
  modalId: number;
  data: Data;
  dispatchCloseModalById: (...args: Parameters<typeof closeModalById>) => void;
  isMobile: boolean;
  root: Document | ShadowRoot;
  isControlled: boolean;
  theme: DefaultTheme;
  scope: string[];
}

export interface ModalComposedProps<Data = {}> extends Omit<ModalProps<Data>, 'modalId'>, ModalInternalsProps<Data> {}

export interface ModalMock {}

interface ModalState {
  scrollOffset: number;
}

const ModalDefaultProps = {
  isClosable: true,
  noheader: false,
  nopadding: false,
  forceRender: false,
  align: 'left',
  maxWidth: MAX_MODAL_WIDTH,
};

export type ModalStatics = {
  Content: typeof Content;
  Footer: typeof Footer;
};

/**
 * @since 0.19.0
 */
class Modal<Data> extends React.PureComponent<ModalComposedProps<Data>, ModalState> {
  parentSelector: () => HTMLElement;

  constructor(props: ModalComposedProps<Data>) {
    super(props);
    this.state = {
      scrollOffset: null,
    };

    const { root } = props;

    if (!Modal.appElement) {
      const element: HTMLElement = root.querySelector(`#${getAppContainerId()}`);
      Modal.setAppElement(element);
    }

    this.parentSelector = () => (
      (root === document ? root.body : root) as unknown as HTMLElement
    );
  }

  componentDidMount() {
    this.handleMainScroll();
  }

  componentDidUpdate(prevProps: ModalComposedProps<Data>) {
    const { onOpen, onClose, isOpened, isControlled } = this.props;

    if (isOpened !== prevProps.isOpened) {
      if (onOpen && isOpened) {
        onOpen();
      } else if (onClose && !isOpened && !isControlled) {
        onClose();
      }
    }
  }
  getSnapshotBeforeUpdate(prevProps: ModalComposedProps<Data>): null {
    const { isOpened } = this.props;

    if (isOpened !== prevProps.isOpened) {
      this.handleMainScroll();
    }

    return null;
  }

  handleMainScroll() {
    const { isOpened } = this.props;

    if (isOpened) {
      this.setState({
        scrollOffset: window.innerWidth - document.body.clientWidth,
      });
    }
  }

  closeModal = () => {
    const {
      modalId, dispatchCloseModalById, isControlled, onClose,
    } = this.props;

    if (!isControlled) {
      dispatchCloseModalById(modalId);
    } else {
      onClose();
    }
  };

  resolveModalContext = memoize((data) => ({ childModal: true, data }));

  resolveChildren() {
    const { forceRender, isOpened, children, name, modalId, data } = this.props;

    if (typeof children === 'function') {
      if (forceRender || isOpened) return children({ name, modalId, data });
      return null;
    }

    return children;
  }

  render() {
    const {
      name, Icon, isClosable, isOpened, modalId, modalTitle, children, nopadding, noheader, headerBackground, isMobile, align, theme, forceRender, data, shouldCloseOnOverlayClick, shouldCloseOnEsc, root, maxWidth, scrollInsideModal, ...props
    } = this.props;
    const { scrollOffset } = this.state;

    const resolvedChildren = this.resolveChildren();

    const content = excludeChildrenByType(resolvedChildren, Header, Footer);
    const contentNopadding = getPropByType(resolvedChildren, Content, 'nopadding', nopadding) as boolean;
    const header = findChildByType(resolvedChildren, Header);
    const footer = findChildByType(resolvedChildren, Footer);
    const footerNopadding = get(footer, 'props.nopadding', nopadding);
    const hasHeader = Boolean(!noheader && (!theme?.Modal.noHeaderIfClosable || isClosable));
    const hasContentHeader = Boolean(header);
    const hasFooter = Boolean(footer);

    if (!forceRender && !isOpened) {
      return null;
    }

    return (
      <ModalContext.Consumer>
        {({ childModal }) => (
          <ModalContext.Provider value={this.resolveModalContext(data)}>
            {!childModal && (
              <ModalGlobalStyles
                scrollOffset={scrollOffset}
                isClosable={isClosable}
                hasHeader={hasHeader}
                maxWidth={maxWidth}
                scrollInsideModal={scrollInsideModal}
              />
            )}
            <ReactModal
              isOpen={isOpened}
              className="iA_Core_Modal"
              parentSelector={this.parentSelector}
              overlayClassName="iA_Core_Modal-Overlay"
              onRequestClose={this.closeModal}
              shouldCloseOnOverlayClick={typeof shouldCloseOnOverlayClick !== 'undefined' ? shouldCloseOnOverlayClick : isClosable}
              shouldCloseOnEsc={typeof shouldCloseOnEsc !== 'undefined' ? shouldCloseOnEsc : isClosable}
              testId="Modal"
              data={{ 'analytics-scope': props['data-analytics-scope'] }}
              {...props}
            >
              {hasHeader && (
                <StyledModalHeader headerBackground={headerBackground}>
                  <Row nogutter align="center">
                    {Icon ? <Col p-l={16} xs="content"><StyledHeaderIcon as={Icon} /></Col> : undefined}
                    <Col p={16}>{modalTitle}</Col>
                    <Col xs="content">
                      {isClosable && (
                        <StyledCloseButton
                          white
                          data-testid="ModalClosebutton"
                          onClick={this.closeModal}
                        />
                      )}
                    </Col>
                  </Row>
                </StyledModalHeader>
              )}
              {hasContentHeader && <StyledContentHeader nopadding={footerNopadding} isMobile={isMobile}>{header}</StyledContentHeader>}
              <StyledModalContentWrapper
                scrollInsideModal={scrollInsideModal}
              >
                <StyledModalContent
                  nopadding={contentNopadding}
                  hasFooter={hasFooter}
                  isMobile={isMobile}
                  align={align}
                  hasContentHeader={hasContentHeader}
                  scrollInsideModal={scrollInsideModal}
                >
                  {content.map((child, index) => <React.Fragment key={`${index}`}>{child}</React.Fragment>)}
                </StyledModalContent>
                {hasFooter && <StyledFooter nopadding={footerNopadding} isMobile={isMobile}>{footer}</StyledFooter>}
              </StyledModalContentWrapper>

            </ReactModal>
          </ModalContext.Provider>
        )}
      </ModalContext.Consumer>
    );
  }

  static appElement: HTMLElement | string;
  static Header = Header;
  static Content = Content;
  static Footer = Footer;
  static defaultProps = ModalDefaultProps;
  static publicProps = ['name', 'modalTitle', 'Icon', 'nopadding', 'isClosable', 'noheader', 'isOpened', 'initialIsOpened', 'onOpen', 'onClose', 'headerBackground', 'align'] as StringKeyOf<ModalProps<{}>>[];

  static setAppElement = (appElement: HTMLElement | string) => {
    Modal.appElement = appElement;
    ReactModal.setAppElement(appElement);
  };
}

export default Modal;
